import classnames from 'classnames';
import css from 'styled-jsx/css';
import PropTypes from 'prop-types';
import Router from 'next/router';

import { useState } from 'react';
import { dimensions } from '../../common/Theme';
import Card from '../../common/layout/Card';
import quotesApi from '../../../services/api/quotes';
import Button from '../../common/elements/Button';
import DateControl from '../../common/forms/DateControl';
import CountrySelector from '../../common/elements/CountrySelector';
import format from 'date-fns/format';
import { formatter } from '@yupwego/string-fns';
import startOfDay from 'date-fns/startOfDay';
import parseISO from 'date-fns/parseISO';
import countryCodeOf from '../../../lib/js/countryCodeOf';
import {
  mixed,
  strings,
  dates,
  validate
} from '../../common/forms/Validations';
import Form from '../../common/forms/Form';
import uuid from 'uuid';
import IconButton from '../../common/elements/IconButton';
import { HREF as TRAVELERS_HREF } from '../../../../pages/assurance/simulation/voyageurs';
import Pricer from '@yupwego/pricer';

const DESKTOP_BREAKPOINT = 600;
const INPUT_GAP = '16px';

const styles = css`
  .dates {
    display: flex;
    flex-direction: column;
  }
  .date {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
  .date-hidden {
    display: none;
  }
  .stays {
    list-style: none;
    counter-increment: stays;
    padding-left: 6px;
  }
  .stay {
    display: flex;
    margin-bottom: 20px;
    width: 100%;
    align-items: center;
    font-size: 1.125rem;
  }

  .stay-infos {
    display: flex;
    margin: 0;
    padding: 0;
    flex-direction: column;
    flex-grow: 1;
    justify-content: space-between;
  }
  .stay-date,
  .stay-emphasized {
    font-weight: bold;
  }
  .stay-date {
    margin: 0 18px;
  }
  .stay-country,
  .stay-dates {
    display: flex;
    flex-wrap: wrap;
  }
  .stay-country {
    line-height: 24px;
  }
  .stay-flag {
    margin: auto 8px auto 0;
    border-radius: 10%;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    .dates {
      flex-direction: row;
    }
    .date-multi {
      width: 50%;
    }

    .date-multi:nth-child(even) {
      margin-left: ${INPUT_GAP};
    }

    .date-multi:nth-child(odd) {
      margin-right: ${INPUT_GAP};
    }

    .stay-infos {
      flex-direction: row;
    }
  }
`;
const embeddedStyles = css.resolve`
  .country {
    margin-bottom: ${dimensions.ywgFormSpacing};
  }
  .stay-delete.stay-delete {
    margin: 0 0 0 20px;
    width: 48px;
    height: 48px;
    flex-shrink: 0;
  }
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    .stay-delete.stay-delete {
      margin-left: 0 0 0 40px;
      width: 24px;
      height: 24px;
    }
  }
`;
function newStay(initialDate = new Date()) {
  return {
    startDate: format(initialDate, 'yyyy-MM-dd'),
    endDate: '',
    destination: '',
    key: uuid.v1()
  };
}
const stayValidators = countries => value => {
  const today = startOfDay(new Date());
  return {
    destination: [
      strings.required.withError(''),
      mixed.oneOf(...countries.map(c => c.label))
    ],
    startDate: [
      mixed.required.withError(''),
      dates
        .isAfter(today)
        .withError(
          'La date de départ doit être dans le futur (une souscription immédiate ne prends effet que le lendemain)'
        )
    ],
    endDate: [
      mixed.required.withError(''),
      dates
        .isAfter(today)
        .withError('La date de retour doit être dans le futur'),
      dates
        .isAfter(value.startDate ? parseISO(value.startDate) : today)
        .withError('la date de retour ne peut être avant la date de départ')
    ]
  };
};

const Destinations = ({ className, countries, prices, quote }) => {
  const [travel, setTravel] = useState(quote.steps);
  const lastStay = travel.slice(-1)[0];
  const stay = newStay(lastStay ? parseISO(lastStay.endDate) : new Date());

  const [continueSubmitting, setContinueSubmitting] = useState(false);
  const canContinue = travel.length > 0;
  const continueClicked = async () => {
    if (continueSubmitting === true) {
      return false;
    }
    Router.push(TRAVELERS_HREF);
  };
  const onAdd = stay => {
    const travelUpdated = [...travel, stay];
    setTravel(travelUpdated);
    sendTravel(travelUpdated);
  };
  const removeStay = stay => {
    const travelUpdated = travel.filter(s => s.key != stay.key);
    setTravel(travelUpdated);
    sendTravel(travelUpdated);
  };
  const sendTravel = travel => {
    setContinueSubmitting(true);
    return quotesApi
      .updateSteps(quote.id, travel)
      .then(() => setContinueSubmitting(false));
  };

  return (
    <div
      className={classnames(
        'form',
        { 'form--continue': canContinue },
        className
      )}
    >
      <Card squared className={classnames('panel', className)}>
        {canContinue ? <h2>Vos destinations</h2> : null}
        {travel.length ? (
          <ol className="stays">
            {travel.map(stay => (
              <li key={stay.key} className="stay">
                <ul className="stay-infos">
                  <li className="stay-country">
                    <span
                      className={`fflag ff-md ff-lt fflag-${countryCodeOf(
                        stay.destination,
                        countries
                      )} stay-flag`}
                    />
                    <span className="stay-emphasized">{stay.destination}</span>
                    {' '}(zone 
                    {Pricer.areaOf(stay.destination, prices)})
                  </li>
                  <li className="stay-dates">
                    Du{' '}
                    <span className="stay-date">
                      {formatter(stay.startDate)}
                    </span>{' '}
                    au{' '}
                    <span className="stay-date">{formatter(stay.endDate)}</span>
                  </li>
                </ul>
                <IconButton
                  icon="trash"
                  alt={`supprimer      'Content-Type': 'application/json'
      },
      body
     ${stay.destination} du ${stay.startDate} au ${stay.endDate}`}
                  title={`supprimer ${stay.destination} du ${stay.startDate} au ${stay.endDate}`}
                  className={classnames(
                    'stay-delete',
                    embeddedStyles.className
                  )}
                  id={stay.key}
                  onClick={() => removeStay(stay)}
                />
              </li>
            ))}
          </ol>
        ) : null}
        {canContinue ? (
          <h2 className={classnames('title', className)}>Ajoutez un séjour</h2>
        ) : (
          <h2 className={classnames('title', className)}>
            Sélectionnez votre première destination
          </h2>
        )}
        <Form
          key={stay.key}
          initialValues={stay}
          validate={value => validate(value, stayValidators(countries)(value))}
          onSubmit={onAdd}
        >
          {({ values, metas, onChange, onBlur, doSubmit }) => (
            <form onSubmit={doSubmit}>
              <CountrySelector
                label="CHOISISSEZ LE PAYS"
                name="destination"
                placeholder="Pays..."
                countries={countries}
                className={classnames('country', embeddedStyles.className)}
                value={values.destination}
                valid={metas.destination.valid}
                error={metas.destination.error}
                onChange={onChange}
                onBlur={onBlur}
                required
                autofocus
                autoComplete="off"
              />
              <div className="dates">
                <div
                  className={classnames('date date-multi', {
                    'date-hidden': canContinue
                  })}
                >
                  <DateControl
                    label="DATE D’ARRIVÉE DANS LE PAYS"
                    name="startDate"
                    mode="travel"
                    value={values.startDate}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={metas.startDate.error}
                    valid={metas.startDate.valid}
                    required
                  />
                </div>
                <div
                  className={classnames('date', {
                    'date-multi': !canContinue
                  })}
                >
                  <DateControl
                    label="DATE DE DÉPART DU PAYS"
                    name="endDate"
                    mode="travel"
                    value={values.endDate}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={metas.endDate.error}
                    valid={metas.endDate.valid}
                    required
                  />
                </div>
              </div>
              <Button
                className={classnames('action', className)}
                type="submit"
                shadowed
              >
                Ajouter
              </Button>
            </form>
          )}
        </Form>
      </Card>
      <div
        className={classnames('bottom-actions bottom-actions--end', className)}
      >
        {canContinue ? (
          <Button
            className={classnames('continue', className)}
            type="button"
            arrow={continueSubmitting ? null : 'right'}
            shadowed
            submitting={continueSubmitting}
            onClick={continueClicked}
            disabled={continueSubmitting}
          >
            Continuer
          </Button>
        ) : null}
      </div>
      <style jsx>{styles}</style>
      {embeddedStyles.styles}
    </div>
  );
};

Destinations.propTypes = {
  className: PropTypes.string.isRequired,
  countries: PropTypes.array.isRequired,
  prices: PropTypes.object.isRequired
};

export default Destinations;
